import type { BillPeriod } from "../../../shared/utils/billPeriods";
import type {
	WaterBillDraft,
	WaterBillsData,
	WaterBillTrackedGallons,
	WaterReadType,
} from "../models/WaterBilling";

export interface WaterBillGridRow {
	id: string;
	billId: number;
	isFirstOfBill: boolean;
	billRowCount: number;
	// meter
	meterId: number | null;
	meterNumber: string;
	meterSize: string | null;
	gallons: number | null;
	trackedGallons: number | null;
	startReadType: WaterReadType | null;
	endReadType: WaterReadType | null;
	// bill
	servicePeriodStart: Date | null;
	servicePeriodEnd: Date | null;
	totalGallons: number | null;
	trackedTotalGallons: number | null;
	balanceForward: number | null;
	currentBill: number | null;
	checkNumber: string | null;
	payDate: Date | null;
	percentByTarget: Record<string, number | null>;
	breakdownByTarget: Record<string, number | null>;
	costByTarget: Record<string, number | null>;
}

export const buildWaterBillGridRows = (
	data: WaterBillsData,
): WaterBillGridRow[] =>
	data.bills.flatMap((bill) => {
		const meters = data.meters.filter((meter) => meter.BillId === bill.BillId);
		const allocations = data.allocations.filter(
			(allocation) => allocation.BillId === bill.BillId,
		);

		const percentByTarget: Record<string, number | null> = {};
		const breakdownByTarget: Record<string, number | null> = {};
		const costByTarget: Record<string, number | null> = {};
		for (const allocation of allocations) {
			percentByTarget[allocation.TargetCode] = allocation.AllocationPercent;
			breakdownByTarget[allocation.TargetCode] = allocation.AllocatedGallons;
			costByTarget[allocation.TargetCode] = allocation.AllocatedAmount;
		}

		const lines = meters.length > 0 ? meters : [null];

		return lines.map((meter, index) => ({
			id: `${bill.BillId}:${meter?.MeterId ?? "none"}`,
			billId: bill.BillId,
			isFirstOfBill: index === 0,
			billRowCount: lines.length,
			meterId: meter?.MeterId ?? null,
			meterNumber: meter?.MeterNumber ?? "—",
			meterSize: meter?.MeterSize ?? null,
			gallons: meter?.Gallons ?? null,
			trackedGallons: meter?.TrackedGallons ?? null,
			startReadType: meter?.StartReadType ?? null,
			endReadType: meter?.EndReadType ?? null,
			servicePeriodStart: index === 0 ? bill.ServicePeriodStart : null,
			servicePeriodEnd: index === 0 ? bill.ServicePeriodEnd : null,
			totalGallons: index === 0 ? bill.TotalGallons : null,
			trackedTotalGallons: index === 0 ? bill.TrackedGallons : null,
			balanceForward: index === 0 ? bill.BalanceForward : null,
			currentBill: index === 0 ? bill.CurrentBill : null,
			checkNumber: index === 0 ? bill.CheckNumber : null,
			payDate: index === 0 ? bill.PayDate : null,
			percentByTarget: index === 0 ? percentByTarget : {},
			breakdownByTarget: index === 0 ? breakdownByTarget : {},
			costByTarget: index === 0 ? costByTarget : {},
		}));
	});

export const recalculateBillTotals = (
	rows: WaterBillGridRow[],
	billId: number,
): WaterBillGridRow[] => {
	const meterLines = rows.filter(
		(row) => row.billId === billId && row.meterId !== null,
	);

	const totalGallons = meterLines.some((row) => row.gallons !== null)
		? meterLines.reduce((sum, row) => sum + (row.gallons ?? 0), 0)
		: null;

	return rows.map((row) => {
		if (row.billId !== billId || !row.isFirstOfBill) return row;

		const billedAmount =
			row.currentBill === null
				? null
				: row.currentBill - (row.balanceForward ?? 0);

		const breakdownByTarget: Record<string, number | null> = {};
		const costByTarget: Record<string, number | null> = {};
		for (const [code, percent] of Object.entries(row.percentByTarget)) {
			breakdownByTarget[code] =
				totalGallons === null || percent === null
					? null
					: (totalGallons * percent) / 100;
			costByTarget[code] =
				billedAmount === null || percent === null
					? null
					: (billedAmount * percent) / 100;
		}

		return { ...row, totalGallons, breakdownByTarget, costByTarget };
	});
};

export const applyTrackedGallons = (
	rows: WaterBillGridRow[],
	billId: number,
	tracked: WaterBillTrackedGallons,
): WaterBillGridRow[] =>
	rows.map((row) =>
		row.billId !== billId
			? row
			: {
					...row,
					trackedGallons:
						row.meterId === null
							? row.trackedGallons
							: (tracked.byMeterId[row.meterId] ?? null),
					trackedTotalGallons: row.isFirstOfBill
						? tracked.total
						: row.trackedTotalGallons,
				},
	);

export const getWaterBillPeriods = (data: WaterBillsData): BillPeriod[] =>
	data.bills.map((bill) => ({
		billId: bill.BillId,
		start: bill.ServicePeriodStart,
		end: bill.ServicePeriodEnd,
	}));

export const buildWaterBillDraft = (
	waterAccountId: number,
	rows: WaterBillGridRow[],
	targetCodes: string[],
): WaterBillDraft => {
	const header = rows.find((row) => row.isFirstOfBill) ?? rows[0];

	return {
		BillId: header.billId,
		WaterAccountId: waterAccountId,
		ServicePeriodStart: header.servicePeriodStart,
		ServicePeriodEnd: header.servicePeriodEnd,
		BalanceForward: header.balanceForward,
		CurrentBill: header.currentBill,
		CheckNumber: header.checkNumber,
		PayDate: header.payDate,
		Meters: rows
			.filter((row) => row.meterId !== null)
			.map((row) => ({
				MeterId: row.meterId as number,
				Gallons: row.gallons,
				StartReadType: row.startReadType,
				EndReadType: row.endReadType,
			})),
		Allocations: targetCodes
			.filter((code) => typeof header.percentByTarget[code] === "number")
			.map((code) => ({
				TargetCode: code,
				AllocationPercent: header.percentByTarget[code] as number,
			})),
	};
};
